import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import { PrepaidCard, PrepaidCardResponse, PrepaidCardsResponse } from '@models/prepaidCard';
import { QueryClient } from '@tanstack/react-query';

/** list와 겹치는 detail 필드만 골라 패치한다. */
function getDetailPatch(prev: PrepaidCard, next: PrepaidCard): Partial<PrepaidCardResponse> | null {
    const patch: Partial<PrepaidCardResponse> = {};
    if (prev.name !== next.name) patch.name = next.name;
    if (prev.balance !== next.balance) patch.balance = next.balance;
    if (prev.isFavorites !== next.isFavorites) patch.isFavorites = next.isFavorites;
    if (prev.imageUrl !== next.imageUrl) patch.imageUrl = next.imageUrl;
    if (prev.barcodeNo !== next.barcodeNo) patch.barcodeNo = next.barcodeNo;
    if (prev.expiredAt !== next.expiredAt) patch.expiredAt = next.expiredAt;
    if (prev.status !== next.status) patch.status = next.status;
    return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * /prepaid-cards 리스트 캐시를 setQueryData로 갱신한다.
 * 변경·삭제된 카드의 detail(id) 캐시도 함께 맞춘다.
 */
function setUserPrepaidCardList(
    queryClient: QueryClient,
    updater: (cards: PrepaidCard[]) => PrepaidCard[],
) {
    const listKey = userPrepaidCardQueries.list().queryKey;
    const prevCards = queryClient.getQueryData<PrepaidCardsResponse>(listKey)?.cards ?? [];

    queryClient.setQueryData<PrepaidCardsResponse>(
        listKey,
        (prev) => (prev ? { ...prev, cards: updater(prev.cards) } : prev),
    );

    const nextCards = queryClient.getQueryData<PrepaidCardsResponse>(listKey)?.cards ?? [];
    const nextById = new Map(nextCards.map((card) => [card.id, card]));

    prevCards.forEach((prev) => {
        const next = nextById.get(prev.id);
        if (!next) {
            queryClient.removeQueries({ queryKey: userPrepaidCardQueries.detail(prev.id).queryKey });
            return;
        }
        const patch = getDetailPatch(prev, next);
        if (!patch) return;
        queryClient.setQueryData<PrepaidCardResponse>(
            userPrepaidCardQueries.detail(prev.id).queryKey,
            (detail) => (detail ? { ...detail, ...patch } : detail),
        );
    });
}

export default setUserPrepaidCardList;
